import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

/* =========================================================
   AXIOS INSTANCE
========================================================= */
const api = axios.create({
  baseURL:
    process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:5000/api",
});

/* =========================================================
   FETCH NOTIFICATIONS
========================================================= */
export const fetchNotifications = createAsyncThunk(
  "notification/fetchNotifications",
  async ({ userId, unreadOnly = false }, { rejectWithValue }) => {
    try {
      if (!userId) return rejectWithValue("User ID required");

      const res = await api.get(
        `/notifications/${userId}?unread=${unreadOnly}`
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch notifications"
      );
    }
  }
);

/* =========================================================
   MARK AS READ
========================================================= */
export const markNotificationAsRead = createAsyncThunk(
  "notification/markAsRead",
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      const res = await api.patch(
        `/notifications/read/${notificationId}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to mark as read"
      );
    }
  }
);

/* =========================================================
   MARK ALL AS READ
========================================================= */
export const markAllAsRead = createAsyncThunk(
  "notification/markAllAsRead",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.patch(
        `/notifications/mark-all-read/${userId}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to mark all as read"
      );
    }
  }
);

/* =========================================================
   DELETE NOTIFICATION
========================================================= */
export const deleteNotification = createAsyncThunk(
  "notification/delete",
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      return notificationId;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete"
      );
    }
  }
);

/* =========================================================
   CLEAR ALL NOTIFICATIONS
========================================================= */
export const clearUserNotifications = createAsyncThunk(
  "notification/clearAll",
  async (userId, { rejectWithValue }) => {
    try {
      await api.delete(`/notifications/user/${userId}`);
      return userId;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to clear"
      );
    }
  }
);

/* =========================================================
   INITIAL STATE
========================================================= */
const initialState = {
  items: [],
  loading: false,
  error: null,
};

/* =========================================================
   SLICE
========================================================= */
const notificationSlice = createSlice({
  name: "notification",
  initialState,

  reducers: {
    /* ================= REALTIME ADD ================= */
    addNotification: (state, action) => {
      const n = action.payload;
      if (!n) return;

      const exists = state.items.find(
        (i) => i._id === n._id || i.id === n.id
      );

      if (!exists) {
        state.items.unshift({
          ...n,
          isRead: n.isRead ?? false,
        });
      }
    },

    /* ================= REMOVE ================= */
    removeNotification: (state, action) => {
      state.items = state.items.filter(
        (i) => i._id !== action.payload && i.id !== action.payload
      );
    },

    /* ================= DISMISS ================= */
    dismissNotification: (state, action) => {
      const item = state.items.find(
        (i) => i._id === action.payload || i.id === action.payload
      );
      if (item) item.dismissed = true;
    },

    /* ================= LOCAL MARK READ ================= */
    markAsRead: (state, action) => {
      const id =
        typeof action.payload === "string"
          ? action.payload
          : action.payload?._id;

      const item = state.items.find(
        (i) => i._id === id || i.id === id
      );

      if (item) item.isRead = true;
    },

    /* ================= CLEAR UI ================= */
    clearAllNotifications: (state) => {
      state.items = [];
    },
  },

  extraReducers: (builder) => {
    builder

      /* ================= FETCH ================= */
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;

        console.log("📦 Notifications Payload:", action.payload);

        const res = action.payload;

        const notifications = res?.data || res?.notifications || [];

        state.items = Array.isArray(notifications)
          ? notifications.map((n) => ({
              ...n,
              isRead: n.isRead ?? false,
            }))
          : [];
      })

      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ================= MARK SINGLE ================= */
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const id =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?._id;

        const item = state.items.find(
          (i) => i._id === id || i.id === id
        );

        if (item) item.isRead = true;
      })

      /* ================= MARK ALL ================= */
      .addCase(markAllAsRead.fulfilled, (state) => {
        state.items = state.items.map((n) => ({
          ...n,
          isRead: true,
        }));
      })

      /* ================= DELETE ================= */
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.items = state.items.filter(
          (n) => n._id !== action.payload && n.id !== action.payload
        );
      })

      /* ================= CLEAR ALL ================= */
      .addCase(clearUserNotifications.fulfilled, (state) => {
        state.items = [];
      });
  },
});

/* =========================================================
   EXPORT ACTIONS
========================================================= */
export const {
  addNotification,
  removeNotification,
  dismissNotification,
  markAsRead,
  clearAllNotifications,
} = notificationSlice.actions;

/* =========================================================
   EXPORT REDUCER
========================================================= */
export default notificationSlice.reducer;
